import { ReactNode, ChangeEvent, FocusEvent, memo, MouseEvent } from 'react';
import styled from 'styled-components';
import Button from 'components/ui-components/Button';

export interface QuantitySelectorProps {
  value: number;
  width?: number;
  incrementValue?: number;
  id?: string;
  label?: string;
  disabled?: boolean;
  incrementIcon?: ReactNode;
  decrementIcon?: ReactNode;
  className?: string;
  stopButtonPropagation?: boolean;
  testIdPrefix?: string;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  handleIncrement?: (value: number) => void;
  handleDecrement?: (value: number) => void;
  hideButtons?: boolean;
}

interface QuantitySelectorStyle {
  width?: number;
}

const StyledQuantitySelector = styled.div<QuantitySelectorStyle>`
  width: ${({ width }) => width}px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  border: 1px solid black;
  padding: 0.6875rem;
`;

const QuantityInput = styled.input`
  box-sizing: border-box;
  width: 50%;
  margin: 0 auto;
  border: 0;
  text-align: center;

  // hiding default browser arrows
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const QuantityButton = styled(Button)`
  width: 28px;
  height: 28px;
  min-height: 0;
  padding: 0.5rem;
`;

const QuantitySelector = memo(
  ({
    value,
    width = 155,
    incrementValue,
    id = 'quantity-selector',
    label,
    disabled,
    incrementIcon,
    decrementIcon,
    className,
    stopButtonPropagation,
    handleBlur,
    handleIncrement,
    handleDecrement,
    handleChange,
    testIdPrefix,
    hideButtons,
  }: QuantitySelectorProps) => {
    /**
     * reset to intial value if value is less than minimum
     * @param val
     * @returns val
     */
    const checkInvalidValue = (val: number) => {
      if (incrementValue && val < incrementValue) return incrementValue;
      if (!incrementValue && val <= 1) return 1;

      return val;
    };

    /**
     * increment by incrementValue or 1
     */
    const increment = (e?: MouseEvent) => {
      if (stopButtonPropagation && e) {
        e.stopPropagation();
      }
      if (handleIncrement) {
        const newValue = incrementValue ? value + incrementValue : value + 1;
        handleIncrement(newValue);
      }
    };

    /**
     * decrement by incrementValue or 1
     */
    const decrement = (e?: MouseEvent) => {
      if (stopButtonPropagation && e) {
        e.stopPropagation();
      }
      if (handleDecrement) {
        const newValue = incrementValue ? value - incrementValue : value - 1;
        handleDecrement(checkInvalidValue(newValue));
      }
    };

    return (
      <StyledQuantitySelector
        width={width}
        className={
          className
            ? `${className} cef-quantity-selector`
            : 'cef-quantity-selector'
        }>
        {!hideButtons && (
          <QuantityButton
            label="decrease quantity"
            handleClick={decrement}
            disabled={disabled}
            className="cef-quantity-selector__button cef-quantity-selector__button--decrement"
            testId={
              testIdPrefix
                ? `${testIdPrefix}-quantity-decrease`
                : 'quantity-decrease'
            }>
            {decrementIcon || '-'}
          </QuantityButton>
        )}
        <QuantityInput
          type="number"
          id={id}
          name="quantity-selector"
          aria-label={label || 'quantity selector'}
          onBlur={handleBlur}
          onChange={handleChange}
          value={value}
          disabled={disabled}
          className="cef-quantity-selector__input"
          data-testid={
            testIdPrefix ? `${testIdPrefix}-quantity-input` : 'quantity-input'
          }
        />
        {!hideButtons && (
          <QuantityButton
            label="increase quantity"
            handleClick={increment}
            disabled={disabled}
            className="cef-quantity-selector__button cef-quantity-selector__button--increment"
            testId={
              testIdPrefix
                ? `${testIdPrefix}-quantity-increase`
                : 'quantity-increase'
            }>
            {incrementIcon || '+'}
          </QuantityButton>
        )}
      </StyledQuantitySelector>
    );
  },
);

QuantitySelector.displayName = 'QuantitySelector';

export default QuantitySelector;
